import { FileText, Paperclip, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'shared/components/ui/use-toast';
import useThreadsStore from 'shared/store/threadsStore';
import styles from './sendEmailAction.module.scss';
import { FinecoButton } from '../../FinecoButton';
import { useChatActions } from '@dev-streetbeat/chat';

type SendEmailActionProps = {
  thread_id: string;
  actionId: string;
  to?: string;
  subject?: string;
  messageText?: string;
  attachments?: string[];
};

const SendEmailAction: React.FC<SendEmailActionProps> = ({
  thread_id: threadId,
  actionId,
  to,
  subject,
  messageText,
  attachments,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [toField, setToField] = useState(to);
  const [subjectField, setSubjectField] = useState(subject);
  const [messageTextField, setMessageTextField] = useState(messageText);
  const [attachmentsList, setAttachmentsList] = useState<string[]>(attachments || []);

  const resetAction = useThreadsStore((state) => state.resetAction);
  const setHideTempStatusUpdate = useThreadsStore((state) => state.setHideTempStatusUpdate);

  const { handleAction } = useChatActions();

  useEffect(() => {
    setHideTempStatusUpdate(true);
  }, []);

  const handleRemoveAttachment = (index: number) => {
    setAttachmentsList((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
    setSubmitting(true);
    const body = {
      to: toField,
      subject: subjectField,
      message_text: messageTextField,
      attachment_ids: [],
    };

    try {
      await handleAction(actionId, body);
      resetAction(threadId);
      setSubmitting(false);
      setHideTempStatusUpdate(false);
      toast({
        title: t('global.success'),
        description: t('email.success'),
      });
    } catch (error) {
      setSubmitting(false);
      toast({
        title: t('global.error'),
        description: error.response?.data?.message || error.message || t('email.error'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>{t('email.composeEmail')}</div>

      <div className={styles.formField}>
        <div className={styles.emailTo}>
          <label htmlFor="email-to" className={styles.emailToLabel}>
            {t('email.to')}
          </label>
          <input
            id="email-to"
            type="email"
            value={toField}
            onChange={(e) => setToField(e.target.value)}
            className={styles.emailToInput}
            placeholder={t('email.toPlaceholder')}
          />
        </div>
      </div>

      <div className={styles.formField}>
        <div className={styles.emailSubject}>
          <label htmlFor="email-subject" className={styles.emailToLabel}>
            {t('email.subject')}
          </label>
          <input
            id="email-subject"
            type="text"
            value={subjectField}
            onChange={(e) => setSubjectField(e.target.value)}
            className={styles.emailSubjectInput}
            placeholder={t('email.subjectPlaceholder')}
          />
        </div>
      </div>

      <div className={styles.messageContainer}>
        <textarea
          id="email-message"
          value={messageTextField}
          onChange={(e) => setMessageTextField(e.target.value)}
          className={styles.messageTextarea}
          placeholder={t('email.messagePlaceholder')}
        />
      </div>

      <div className={styles.attachmentsContainer}>
        <div className={styles.attachmentsHeader}>
          <Paperclip className={styles.iconSmall} />
          <label className={styles.attachmentsLabel}>{t('email.attachments')}</label>
        </div>

        {attachmentsList && attachmentsList.length > 0 ? (
          <div className={styles.attachmentsList}>
            {attachmentsList.map((fileName, index) => (
              <div key={index} className={styles.attachmentChip}>
                <FileText className={styles.iconSmall} />
                <span className={styles.attachmentName}>{fileName}</span>
                <span className={styles.removeButton} onClick={() => handleRemoveAttachment(index)}>
                  <X className={styles.iconSmall} />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.noAttachments}>{t('email.noAttachments')}</p>
        )}
      </div>

      <div className={styles.buttonContainer}>
        {/* <button
          className={styles.sendButton}
          onClick={onSubmit}
          disabled={!toField || !subjectField || submitting}
        >
          {submitting ? t("actions.submitting") : t("actions.submit")}
        </button> */}

        <FinecoButton
          click={onSubmit}
          copy={submitting ? t('actions.submitting') : t('actions.submit')}
          disabled={!toField || !subjectField || submitting}
        />
      </div>
    </div>
  );
};

export default SendEmailAction;
